import { useRef, useState } from 'react';

interface Photo {
  key: string;
  previewUrl: string;
}

interface Props {
  initialKeys?: string[];
  initialUrls?: string[];
  purpose: string;
  onChange: (keys: string[]) => void;
  onRequestUpload: (input: { purpose: string; extension: string; contentType: string }) => Promise<{ uploadUrl: string; key: string }>;
}

export function PhotoGalleryUpload({ initialKeys = [], initialUrls = [], purpose, onChange, onRequestUpload }: Props): JSX.Element {
  const [photos, setPhotos] = useState<Photo[]>(() =>
    initialKeys.map((key, i) => ({ key, previewUrl: initialUrls[i] ?? '' })),
  );
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const notify = (next: Photo[]) => {
    setPhotos(next);
    onChange(next.map((p) => p.key));
  };

  const handleFile = async (file: File) => {
    setUploading(true);
    const ext = (file.name.split('.').pop()?.toLowerCase() ?? 'jpg').slice(0, 5);
    const contentType = file.type || 'image/jpeg';
    try {
      const { uploadUrl, key } = await onRequestUpload({ purpose, extension: ext, contentType });
      await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': contentType } });
      const previewUrl = URL.createObjectURL(file);
      notify([...photos, { key, previewUrl }]);
    } finally {
      setUploading(false);
    }
  };

  const remove = (index: number) => {
    notify(photos.filter((_, i) => i !== index));
  };

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
      {photos.map((photo, i) => (
        <div
          key={photo.key}
          style={{ position: 'relative', width: 80, height: 64, borderRadius: 8, overflow: 'hidden', flexShrink: 0 }}
        >
          <img src={photo.previewUrl} alt={`Photo ${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          <button
            type="button"
            onClick={() => remove(i)}
            style={{
              position: 'absolute',
              top: 3,
              right: 3,
              background: 'rgba(28,43,58,0.65)',
              color: '#fff',
              border: 'none',
              borderRadius: 5,
              padding: '2px 6px',
              fontSize: 10,
              cursor: 'pointer',
            }}
          >
            ✕
          </button>
        </div>
      ))}
      <button
        type="button"
        disabled={uploading}
        onClick={() => inputRef.current?.click()}
        style={{
          width: 80,
          height: 64,
          borderRadius: 8,
          border: '1px dashed #DDE2E7',
          background: '#F7F9FB',
          cursor: uploading ? 'default' : 'pointer',
          fontSize: uploading ? 11 : 20,
          color: '#9FB0BF',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {uploading ? '…' : '+'}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handleFile(file);
          e.target.value = '';
        }}
      />
    </div>
  );
}
